
import React, { useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { addDays, format } from 'date-fns'
import { useDispatch } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { useProject } from '@/Hooks/UseProject'
import { useProjection } from '@/Hooks/ProjectionProvider'
import { useDatePicker } from '@/Hooks/UseDatePicker'
import { getDefaultTaskDates, maxDate, parseLocalDate, safeFormat } from '@/Helpers/DateFunctions'

import { updateTaskAction } from '@/Redux/Actions/ProgrammationManagerActions'
import { updateTaskScheduledDates } from '@/Services/ProgrammationManagerService'

import InputText from '../../components/InputText'

const TaskScheduledDates = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened: task, patchItemOpened, setEditingItem } = useProgrammation()
  const { project } = useProject()
  const { projectedEndDate, extendedNonWorkingDays } = useProjection()

  const { showDateRangePicker, hideDatePicker } = useDatePicker()

  const [ executeDaysReset, setExecuteDaysReset ] = useState(0)

  const startsAt = safeFormat(task.startsAt, 'yyyy-MM-dd')
  const endsAt = safeFormat(task.endsAt, 'yyyy-MM-dd')

  const saveChanges = async (startsAt: string, endsAt: string) => {
    setEditingItem(true)

    const [ error, data ] = await updateTaskScheduledDates(task._id, startsAt, endsAt)

    setEditingItem(false)

    if (error) {
      return false
    }

    patchItemOpened({
      startsAt: data.task.startsAt,
      endsAt: data.task.endsAt,
      executeDays: data.task.executeDays,
    })

    await dispatch(updateTaskAction(data.task, data.updatedTasks))
  }

  const handleChangeDates = async (dates: any) => {

    const {
      startDate,
      endDate
    } = dates

    await saveChanges(startDate, endDate)

    hideDatePicker()
  }

  const handleShowDateRangePicker = () => {

    if (task.hasSubTasks) {
      return
    }

    const defaultDates = getDefaultTaskDates(extendedNonWorkingDays)

    showDateRangePicker({
      startDate: startsAt || defaultDates.startDate,
      endDate: endsAt || defaultDates.endDate,
      projectStartDate: project.startsAt,
      projectEndDate: maxDate(project.endsAt, projectedEndDate) || project.endsAt,
      nonWorkingDays: extendedNonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  const handleChangeExecuteDays = async (value: string) => {

    if (task.hasSubTasks) {
      return
    }

    const newExecuteDays = parseInt(value, 10)

    if (isNaN(newExecuteDays) || newExecuteDays < 1) {
      setExecuteDaysReset(t => t + 1)
      return
    }

    const parsedStartsAt = parseLocalDate(startsAt)

    if (!parsedStartsAt) {
      setExecuteDaysReset(t => t + 1)
      return
    }

    const newEndsAt = format(addDays(parsedStartsAt, newExecuteDays - 1), 'yyyy-MM-dd')

    await saveChanges(startsAt, newEndsAt)
  }

  return (
    <>
      <Row>
        <Col>
          <h5 className='group-title'>
            {t('PROGRAMMATION_MANAGER:ScheduledDatesTitleLabel')}
          </h5>
        </Col>
      </Row>
      <Row>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:ScheduledStartsAtLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(startsAt, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:ScheduledEndsAtLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(endsAt, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:ScheduledExecuteDaysLabel')}
            </Form.Label>
            <InputText
              key={`executeDays-${executeDaysReset}`}
              value={task.executeDays}
              onChange={handleChangeExecuteDays}
              style={{
                padding: '0.375rem !important'
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  )
}

export default TaskScheduledDates
